/**
 * Plano de contas curado para o preenchimento manual guiado.
 *
 * Origem: aba INPUT de "Modelo DRE.xlsx" (172 linhas, ~130 contas folha).
 * Aqui ficam apenas as contas que o consultor pergunta na prática, na mesma
 * ordem em que aparecem na planilha — o questionário segue a sequência do
 * plano de contas, não uma ordem inventada.
 *
 * O usuário pode acrescentar contas fora desta lista em cada grupo
 * ("adicionar outra conta"): elas entram no mesmo grupo e recebem código
 * derivado do grupo pai.
 *
 * Para voltar a exibir uma conta do plano completo, basta acrescentá-la ao
 * grupo correspondente — nada mais precisa mudar no código.
 */

import { contasDoPerfil, contasParaRespostas } from './planoCanonico';

/**
 * Quantidade de meses que o questionário coleta quando ninguém escolhe.
 *
 * É PADRÃO, não constante do aplicativo. O comentário que morava aqui prometia
 * que trocar este número bastava para virar 6 — era falso: `rotulosMeses`
 * fechava sobre ele, então a troca mudaria rótulo e campo do produto inteiro,
 * de todo mundo ao mesmo tempo, e nunca por usuário. Agora a quantidade viaja
 * com o dado (`formData.meses.length`) e este número só decide o começo.
 */
export const MESES_PADRAO = 3;

/**
 * Os ATALHOS de janela que o questionário oferece — não a lista do que é
 * possível escolher.
 *
 * Aqui morava `OPCOES_DE_MESES`, e ela era a lista fechada: quem precisava de
 * sete meses não tinha como pedir sete. Agora a quantidade é digitada livre
 * entre `MESES_MINIMO` e `MESES_MAXIMO`, e estes três números são só o caminho
 * curto para as escolhas comuns. Três é o que o plano gratuito comporta
 * (`backend/services/planos.js`); a régua de quem pode escolher o quê mora em
 * `services/mesesPermitidos.js`, nunca aqui.
 */
export const ATALHOS_DE_MESES = [3, 6, 12];

/**
 * O intervalo em que a quantidade digitada é aceita.
 *
 * O teto de 24 não é regra de negócio, é limite de tela e de rascunho: cada mês
 * é uma página de 32 contas no questionário, um passo no indicador de progresso
 * e mais uma cópia das contas no rascunho do localStorage. Dois anos é o maior
 * levantamento que ainda cabe numa sessão de preenchimento — acima disso o
 * produto entrega uma experiência pior do que a de doze meses de hoje.
 */
export const MESES_MINIMO = 1;
export const MESES_MAXIMO = 24;

/** Prende a quantidade digitada ao intervalo aceito, sem nunca devolver NaN. */
export const limitarQuantidadeDeMeses = (n) => {
  const bruto = Math.floor(Number(n));
  if (!Number.isFinite(bruto) || bruto < MESES_MINIMO) return MESES_MINIMO;
  return Math.min(bruto, MESES_MAXIMO);
};

/**
 * Campos de mês de uma janela de `n` meses: ['mes1', ..., 'mesN'].
 *
 * Substitui o antigo `CAMPOS_MES`, que era uma lista fixa de módulo. A lista
 * fixa foi removida em vez de mantida por compatibilidade: enquanto ela
 * existisse, um ponto novo do código voltaria a depender de três meses sem que
 * nada acusasse.
 */
export const camposDeMes = (n = MESES_PADRAO) => {
  const quantidade = Number(n) > 0 ? Math.floor(Number(n)) : MESES_PADRAO;
  return Array.from({ length: quantidade }, (_, i) => `mes${i + 1}`);
};

const NOMES_MES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

/**
 * Rótulos reais dos meses a partir do mês/ano de referência (o mês mais
 * recente). Ex.: referência junho/2026, quantidade 3 -> Abril, Maio, Junho.
 * É isso que torna a pergunta instrutiva: "quanto pagou em abril?".
 *
 * `quantidade` é o terceiro parâmetro e precisa ser passado por TODO chamador
 * que trabalha fora da janela padrão: um rótulo a menos que o dado desalinha o
 * gráfico em silêncio — a tela mostra números certos com meses errados.
 */
export function rotulosMeses(mesReferencia, anoReferencia, quantidade = MESES_PADRAO) {
  const mes = Number(mesReferencia) || new Date().getMonth() + 1;
  const ano = Number(anoReferencia) || new Date().getFullYear();
  const total = Number(quantidade) > 0 ? Math.floor(Number(quantidade)) : MESES_PADRAO;

  return Array.from({ length: total }, (_, i) => {
    const deslocamento = total - 1 - i; // primeiro campo = mês mais antigo
    const data = new Date(ano, mes - 1 - deslocamento, 1);
    return {
      campo: `mes${i + 1}`,
      mes: data.getMonth() + 1,
      ano: data.getFullYear(),
      curto: `${NOMES_MES[data.getMonth()].slice(0, 3)}/${String(data.getFullYear()).slice(2)}`,
      longo: `${NOMES_MES[data.getMonth()]} de ${data.getFullYear()}`
    };
  });
}

/**
 * Grupos do questionário, na ordem em que são perguntados.
 *
 * categoria: define onde a conta cai na estrutura do DRE consumida pelos
 * dashboards — 'receita' | 'receitaNaoOperacional' | 'variavel' | 'pessoal' |
 * 'socios' | 'administrativa' | 'financeira' | 'marketing'.
 */
/**
 * Grupos do questionário — DERIVADOS do plano de contas canônico.
 *
 * Eles eram uma lista escrita à mão aqui, e essa lista era o TERCEIRO catálogo
 * de contas do produto: a planilha modelo tinha o dela, o motor de indicadores
 * lia prefixos, e o wizard mantinha o seu. Três catálogos divergem no primeiro
 * mês — foi o que aconteceu com os dois leitores espelhados de folha de conta, e
 * está registrado como armadilha no CLAUDE.md.
 *
 * Agora a fonte é uma só (`planoCanonico`), e o questionário é um RECORTE dela
 * pelo perfil. O formato de saída é o mesmo de antes, porque `manualEntryTransform`
 * depende dele:
 *
 *   { id, codigo, titulo, categoria, pergunta, ajuda, contas: [{codigo, nome}] }
 *
 * O `codigo` de cada conta é o LEGADO, e não o canônico: é ele que
 * `manualEntryTransform` usa para separar CMV de imposto sobre venda
 * (`startsWith('3.2.2')`), e é a língua que o motor de indicadores entende.
 */

/** Balde legado -> a identidade que o questionário sempre usou. */
const GRUPO_DO_BALDE = {
  '1.1': { id: 'receitas', titulo: 'Faturamento', categoria: 'receita' },
  '1.2': { id: 'receitasNaoOperacionais', titulo: 'Entradas que não são venda', categoria: 'receitaNaoOperacional' },
  '3.2.2': { id: 'custosVariaveis', titulo: 'Custos variáveis', categoria: 'variavel' },
  '3.2.3.1': { id: 'custosVariaveis', titulo: 'Custos variáveis', categoria: 'variavel' },
  '3.2.3.2': { id: 'custosVariaveis', titulo: 'Custos variáveis', categoria: 'variavel' },
  '3.2.3.3': { id: 'custosVariaveis', titulo: 'Custos variáveis', categoria: 'variavel' },
  '2.1.1.1': { id: 'pessoal', titulo: 'Despesas com pessoal', categoria: 'pessoal' },
  '2.1.1.2': { id: 'socios', titulo: 'Despesas com sócios', categoria: 'socios' },
  '2.1.1.3': { id: 'administrativas', titulo: 'Despesas administrativas', categoria: 'administrativa' },
  '2.1.1.4': { id: 'financeiras', titulo: 'Despesas financeiras', categoria: 'financeira' },
  '2.1.1.5': { id: 'marketing', titulo: 'Despesas com marketing', categoria: 'marketing' }
};

/** A ordem em que o questionário pergunta — a mesma de sempre. */
const ORDEM = [
  'receitas', 'custosVariaveis', 'pessoal', 'socios',
  'administrativas', 'financeiras', 'marketing', 'receitasNaoOperacionais'
];

const PERGUNTA_DO_GRUPO = {
  receitas: {
    pergunta: 'Quanto a empresa faturou em cada mês?',
    ajuda: 'Some tudo que entrou de venda, separado por forma de recebimento. Se não separa por forma, lance tudo em "Vendas à vista".'
  },
  custosVariaveis: {
    pergunta: 'Quanto variou junto com a venda?',
    ajuda: 'Custo que só existe quando você vende: mercadoria, imposto sobre venda, taxa de cartão, comissão.'
  },
  pessoal: {
    pergunta: 'Quanto custou a equipe em cada mês?',
    ajuda: 'Folha e encargos. Não inclua pró-labore de sócio aqui — tem grupo próprio na frente.'
  },
  socios: {
    pergunta: 'Quanto os sócios retiraram?',
    ajuda: 'Pró-labore é retirada fixa mensal. Distribuição de lucro não entra aqui.'
  },
  administrativas: {
    pergunta: 'Quanto custou manter a empresa aberta?',
    ajuda: 'Custo fixo que existe mesmo com venda zero.'
  },
  financeiras: {
    pergunta: 'Quanto o banco levou?',
    ajuda: 'Juros, tarifas e parcelas de empréstimo.'
  },
  marketing: {
    pergunta: 'Quanto investiu para vender?',
    ajuda: 'Divulgação, agência, tráfego pago.'
  },
  receitasNaoOperacionais: {
    pergunta: 'Entrou dinheiro que não foi venda?',
    ajuda: 'Aporte dos sócios, empréstimo captado, resgate de aplicação. Entra no caixa e não é receita.'
  }
};

const baldeDe = (legado) => String(legado).split('.').slice(0, -1).join('.');

/** Monta os grupos do questionário a partir de uma lista de contas canônicas. */
const gruposDasContas = (contas) => {
  const porId = new Map();

  contas
    .filter((conta) => conta.legado)
    .forEach((conta) => {
      const destino = GRUPO_DO_BALDE[baldeDe(conta.legado)];
      if (!destino) return;

      if (!porId.has(destino.id)) {
        porId.set(destino.id, {
          id: destino.id,
          codigo: baldeDe(conta.legado),
          titulo: destino.titulo,
          categoria: destino.categoria,
          ...PERGUNTA_DO_GRUPO[destino.id],
          contas: []
        });
      }
      porId.get(destino.id).contas.push({ codigo: conta.legado, nome: conta.nome });
    });

  return ORDEM.map((id) => porId.get(id)).filter(Boolean);
};

/** Os grupos de um perfil NOMEADO — o desenho antigo, mantido para chamador legado. */
export const gruposDoPerfil = (perfil = 'essencial') => gruposDasContas(contasDoPerfil(perfil));

/**
 * Os grupos que o questionário pergunta, a partir das RESPOSTAS do usuário.
 *
 * É o caminho novo, e o certo: o recorte sai do que a empresa FAZ, não de um
 * nome de tipo de empresa. Sem resposta nenhuma cai no recorte curto, que já
 * produz DRE, margem e ponto de equilíbrio — quem não quiser responder nada
 * continua sendo atendido.
 */
export const gruposParaRespostas = (respostas = {}) =>
  gruposDasContas(contasParaRespostas(respostas));

/**
 * O catálogo padrão do questionário: o recorte de quem não respondeu nada.
 *
 * É o recorte `essencial`, e NÃO o de "ninguém respondeu nada": quem abre o
 * questionário sem passar pelo perfil precisa continuar encontrando a linha de
 * CMV, que só aparece para quem responde "revendo produto pronto". Trocar este
 * padrão tirava a mercadoria do caminho de quem não respondeu nada — medido:
 * quatro testes de reconciliação caíram na hora.
 *
 * Continua exportado porque quatro telas o importam em escopo de módulo. Quem
 * recebe `catalogo` por prop deve usar a prop — este valor é só o começo.
 */
export const GRUPOS = gruposDoPerfil('essencial');

/** Total de contas do catálogo — usado no texto de apresentação do wizard. */
export const TOTAL_CONTAS = GRUPOS.reduce((soma, g) => soma + g.contas.length, 0);

/** Campos do balanço patrimonial (etapa opcional, valor único por campo). */
export const BALANCO_CAMPOS = {
  ativo: [
    { campo: 'disponivel', nome: 'Caixa e bancos', ajuda: 'Dinheiro disponível hoje.' },
    { campo: 'contasReceber', nome: 'Contas a receber', ajuda: 'Vendas já feitas que ainda vão entrar.' },
    { campo: 'estoque', nome: 'Estoque', ajuda: 'Valor de custo do que está parado.' },
    { campo: 'imobilizado', nome: 'Imobilizado', ajuda: 'Máquinas, veículos, imóveis, móveis.' },
    { campo: 'investimentos', nome: 'Investimentos', ajuda: 'Aplicações de longo prazo.' }
  ],
  passivo: [
    { campo: 'fornecedores', nome: 'Fornecedores a pagar', ajuda: 'Compras a prazo em aberto.' },
    { campo: 'emprestimos', nome: 'Empréstimos de curto prazo', ajuda: 'Parcelas que vencem em até 12 meses.' },
    { campo: 'impostos', nome: 'Impostos a pagar', ajuda: 'Tributos vencidos ou a vencer.' },
    { campo: 'emprestimosLP', nome: 'Empréstimos de longo prazo', ajuda: 'Vencem depois de 12 meses.' }
  ],
  patrimonio: [
    { campo: 'total', nome: 'Patrimônio líquido', ajuda: 'Capital dos sócios + lucros acumulados. Deixe em branco para calcular automaticamente (Ativo − Passivo).' }
  ]
};

